import {
  getCompanyName,
  getInitials,
  getProfileDisplayName,
  getIsCompanyUser,
} from '../components/DashboardHeader/profileHelpers';

export { getCompanyName, getInitials, getProfileDisplayName, getIsCompanyUser };

export function getPasswordChangeErrorMessage(error) {
  const message = String(error?.message || error?.name || '').trim();
  const normalized = message.toLowerCase();

  if (normalized.includes('same password') || normalized.includes('cannot be the same')) {
    return 'Your new password must be different from your current password.';
  }
  if (
    normalized.includes('incorrect') ||
    normalized.includes('not authorized') ||
    normalized.includes('invalid password') ||
    normalized.includes('wrong password')
  ) {
    return 'Current password is incorrect.';
  }
  if (normalized.includes('password did not conform') || normalized.includes('policy')) {
    return message || 'Password does not meet the security requirements.';
  }

  return message || 'Failed to change password. Please try again.';
}

export function formatAccountSettingValue(value) {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  return str || null;
}

export function formatRegistrationDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    const str = String(value).trim();
    return str || null;
  }
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export function getRepresentativeName(user) {
  return formatAccountSettingValue(user?.company?.representativeName);
}

export function buildContactRows(user, isCompany, profileForm) {
  if (isCompany) {
    const company = user?.company || {};
    return [
      ['Primary contact', getRepresentativeName(user) || getProfileDisplayName(user, false)],
      ['Role', company.representativePosition],
      ['Email address', user?.email || company.representativeEmail || profileForm.contactEmail],
      ['Phone', company.phoneNumber || company.phone || profileForm.contactPhone],
      ['Website', company.website || profileForm.website],
    ].filter(([, value]) => formatAccountSettingValue(value));
  }

  const details = user?.userDetails || {};
  const firstName = details.firstName || user?.firstName;
  const lastName = details.lastName || user?.lastName;
  const fullName = firstName && lastName ? `${firstName} ${lastName}`.trim() : null;

  return [
    ['Name', fullName || getProfileDisplayName(user, false)],
    ['Email address', user?.email || profileForm.contactEmail],
    ['Phone', details.phoneNumber || profileForm.contactPhone],
    ['Website', profileForm.website],
  ].filter(([, value]) => formatAccountSettingValue(value));
}

export function buildBusinessRegistrationRows(user) {
  const company = user?.company || {};
  return [
    ['Legal name', company.registeredName ?? company.legalName ?? company.name],
    ['Trading name', company.tradeName],
    ['Registration no.', company.registrationNumber ?? company.organizationNumber],
    ['VAT ID', company.vatNumber ?? company.taxNumber ?? company.vatId],
    ['Legal form', company.legalForm],
    ['Founded', formatRegistrationDate(company.registrationDate)],
  ].filter(([, value]) => formatAccountSettingValue(value));
}

const ADDRESS_KEYS = ['line1', 'line2', 'city', 'stateRegionProvince', 'postCode', 'country'];

function countAddressFields(address) {
  if (!address || typeof address !== 'object') return 0;
  return ADDRESS_KEYS.filter((key) => formatAccountSettingValue(address[key])).length;
}

function mergeAddressRecords(...sources) {
  const objects = sources.filter((source) => source && typeof source === 'object');
  if (objects.length === 0) return null;

  const merged = {};
  const ordered = [...objects].sort((a, b) => countAddressFields(b) - countAddressFields(a));

  ordered.forEach((source) => {
    ADDRESS_KEYS.forEach((key) => {
      if (!formatAccountSettingValue(merged[key]) && formatAccountSettingValue(source[key])) {
        merged[key] = source[key];
      }
    });
  });

  return countAddressFields(merged) > 0 ? merged : null;
}

export function getAddressSource(user, isCompany) {
  if (isCompany) {
    const company = user?.company || {};
    return mergeAddressRecords(
      company.registeredAddress,
      company.registered_address,
      company.operatingAddress,
      company.operating_address,
      company.address,
    );
  }

  return user?.userDetails?.address;
}

export function buildAddressRows(address, profileFormAddress) {
  if (address && typeof address === 'object') {
    const street = [address.line1, address.line2].filter(Boolean).join(', ');
    return [
      ['Street', street || null],
      ['Postal', address.postCode],
      ['City', address.city],
      ['State / Region / Province', address.stateRegionProvince],
      ['Country', address.country],
    ].filter(([, value]) => formatAccountSettingValue(value));
  }

  if (formatAccountSettingValue(profileFormAddress)) {
    return [['Address', profileFormAddress]];
  }

  return [];
}

const BANK_ACCOUNT_PENDING_KEY_PREFIX = 'bank_account_update_pending';

export function getBankAccountPendingStorageKey(user) {
  const id = user?.id ?? user?.email;
  return id ? `${BANK_ACCOUNT_PENDING_KEY_PREFIX}_${id}` : null;
}

export function readBankAccountUpdatePending(user) {
  const key = getBankAccountPendingStorageKey(user);
  if (!key) return null;

  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writeBankAccountUpdatePending(user, details) {
  const key = getBankAccountPendingStorageKey(user);
  if (!key) return;

  localStorage.setItem(
    key,
    JSON.stringify({
      submittedAt: new Date().toISOString(),
      iban: details.iban,
      bic: details.bic,
      name: details.name,
      currency: details.currency,
    }),
  );
}

export function validateBankingForm(form) {
  const iban = form.iban?.trim();
  const bic = form.bic?.trim();
  const name = form.name?.trim();
  const currency = form.currency?.trim();

  if (!iban || !bic || !name || !currency) {
    return 'Please complete all required bank account fields.';
  }

  return null;
}

export function normalizeBankingFormPayload(form) {
  return {
    iban: form.iban.trim(),
    bic: form.bic.trim(),
    name: form.name.trim(),
    currency: form.currency.trim(),
  };
}

export function buildBankAccountPendingMessage(pending) {
  if (!pending) return null;

  const submittedAt = pending.submittedAt ? new Date(pending.submittedAt) : null;
  const submittedLabel =
    submittedAt && !Number.isNaN(submittedAt.getTime())
      ? submittedAt.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
      : null;

  const details = [pending.name, pending.iban, pending.currency].filter(Boolean).join(' · ');
  return submittedLabel
    ? `Submitted on ${submittedLabel}${details ? ` (${details})` : ''}.`
    : details || null;
}

export function buildAccountRows(user, bankAccount) {
  const accountName = bankAccount?.accountHolderName || bankAccount?.beneficiaryName || bankAccount?.name;
  return [
    ['Customer no.', user?.id],
    ['Account name', accountName],
    ['IBAN', bankAccount?.iban],
  ].filter(([, value]) => formatAccountSettingValue(value));
}

function shouldShowProfileRow(row, editing) {
  if (editing && row.editable) return true;
  return formatAccountSettingValue(row.value);
}

function formatAddressForProfileForm(addressSource) {
  if (!addressSource) return '';
  if (typeof addressSource === 'string') return addressSource;

  const parts = [
    addressSource.line1,
    addressSource.line2,
    [addressSource.postCode, addressSource.city].filter(Boolean).join(' '),
    addressSource.stateRegionProvince,
    addressSource.country,
  ].filter((part) => formatAccountSettingValue(part));

  return parts.join(', ');
}

export function buildProfileContactRows(user, isCompany, profileForm, editing) {
  const rows = [];

  if (isCompany) {
    rows.push({
      label: 'Company name',
      value: profileForm.companyName || '',
      field: 'companyName',
      editable: true,
    });
  }

  rows.push(
    {
      label: 'Contact email',
      value: profileForm.contactEmail || '',
      field: 'contactEmail',
      editable: true,
      inputType: 'email',
    },
    {
      label: 'Contact phone',
      value: profileForm.contactPhone || '',
      field: 'contactPhone',
      editable: true,
      inputType: 'tel',
    },
    {
      label: 'Website',
      value: profileForm.website || '',
      field: 'website',
      editable: true,
      inputType: 'url',
    },
  );

  return rows.filter((row) => shouldShowProfileRow(row, editing));
}

export function buildProfileAddressRows(user, isCompany, profileForm, editing) {
  const rows = [
    {
      label: isCompany ? 'Business address' : 'Address',
      value: profileForm.address || '',
      field: 'address',
      editable: true,
    },
  ];

  return rows.filter((row) => shouldShowProfileRow(row, editing));
}

export function isValidProfileWebsite(value) {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;

  try {
    const url = trimmed.includes('://') ? trimmed : `https://${trimmed}`;
    const parsed = new URL(url);
    const host = (parsed.hostname || '').toLowerCase();
    if (!host) return false;
    if (host === 'localhost') return true;
    return host.includes('.');
  } catch {
    return false;
  }
}

function omitEmptyStringFields(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== ''));
}

export function buildCompanyUpdatePayloadFromProfileForm(profileForm, user) {
  const existing = user?.company;
  if (!existing) return null;

  const registeredAddress = getAddressSource(user, true) || existing.registeredAddress;
  if (!registeredAddress || !formatAccountSettingValue(registeredAddress.line1)) {
    return null;
  }

  const updates = omitEmptyStringFields({
    email: (profileForm.contactEmail || '').trim(),
    phoneNumber: (profileForm.contactPhone || '').trim(),
    website: (profileForm.website || '').trim(),
    tradeName: (profileForm.companyName || '').trim(),
  });

  const merged = {
    ...existing,
    ...updates,
    registeredAddress: {
      ...(existing.registeredAddress || {}),
      ...registeredAddress,
    },
  };

  if (existing.operatingAddress) {
    merged.operatingAddress = { ...existing.operatingAddress };
  }

  return merged;
}

export function getProfileFormFromUser(user) {
  const isCompany = Boolean(user?.company);

  return {
    companyName: user?.company?.tradeName || user?.company?.registeredName || user?.company?.name || '',
    contactEmail: user?.email || user?.company?.email || '',
    contactPhone: user?.company?.phone || user?.company?.phoneNumber || user?.userDetails?.phoneNumber || '',
    website: user?.company?.website || '',
    address: formatAddressForProfileForm(getAddressSource(user, isCompany)),
  };
}

export function getProfileHeroSubtitle(user, isCompany) {
  if (!isCompany) return null;

  const representative = getRepresentativeName(user);
  const role = formatAccountSettingValue(user?.company?.representativePosition);

  if (representative && role) {
    return { line: `${representative} · `, role };
  }
  if (representative) {
    return { line: representative, role: null };
  }

  return null;
}
